import { FC, useEffect, useMemo } from 'react';

import { ShareClass, useLocale } from '../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../translations/stakeholder-translation.util';
import { Column } from '../../../../types/snapshot.types';
import { StakeholderType } from '../../../../types/stakeholder.types';
import { filterByAllFields } from '../../../../utils/filter-by-all-fields.util';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { mapCapTablePoolShareClassAmounts } from '../../../../utils/pool-share-class-columns.util';
import { calcTotal } from '../../../../utils/reduce-utils.util';
import { toNumber } from '../../../../utils/to-number.util';
import { formatDividendAmount } from '../../../dividends/utils/format-dividend-amount.util';
import { sortStakeholderTypesByOrdering } from '../../utils/sort-cap-table-rows.util';
import { StakeholderTableItem } from '../table-variants/items/stakeholder-table-item';
import {
  DEFAULT_CAP_TABLE_ORDERING,
  getEquityPoolsGrantedDilutedShares,
  getPoolGrantedRowDilutedShares,
  getPoolShareClassAmountTotal,
  getShareClasses,
  isSearchWithoutMatches,
  SnapshotViewByProps,
} from './snapshot-view-by.types';

export const SnapshotViewByType: FC<SnapshotViewByProps> = ({
  snapshot: {
    stats: { stakeholdersTypes, shareClasses, pools, stakeholders },
    issuedShares: issuedTotal,
    dilutedShares: dilutedTotal,
  },
  defaultShareClassId: providedShareClassId,
  ordering = DEFAULT_CAP_TABLE_ORDERING,
  viewBy,
  search,
  visibleColumns,
  setEmptySearchResult,
}) => {
  const { messagesLocale } = useLocale();

  const searchResult = useMemo(
    () =>
      sortStakeholderTypesByOrdering(
        filterByAllFields(stakeholdersTypes, search),
        ordering,
        (type) => messagesLocale[stakeholderTypeTranslation[type]],
      ),
    [messagesLocale, ordering, search, stakeholdersTypes],
  );

  const stakeholdersSearchResult = useMemo(
    () => filterByAllFields(stakeholders, search),
    [stakeholders, search],
  );

  const stakeholderTypesWithDiluted = useMemo(
    () => searchResult.filter((stakeholder) => stakeholder.diluted > 0),
    [searchResult],
  );
  const stakeholdersWithDiluted = useMemo(
    () => stakeholdersSearchResult.filter((stakeholder) => stakeholder.diluted > 0),
    [stakeholdersSearchResult],
  );

  // Skipped entirely when the caller already knows the id — a public viewer cannot make this call.
  const { shareClass } = ShareClass.useDefaultShareClass(!providedShareClassId);

  const defaultShareClassId = providedShareClassId || shareClass?.id || '';

  const { otherShareClasses, defaultShareClass } = getShareClasses(
    defaultShareClassId,
    shareClasses,
  );

  const equityPoolGrantable = pools?.filter(
    (pool) =>
      !pool.isTerminated || new Date(pool?.terminationDate)?.getTime() > new Date().getTime(),
  );
  const terminatedPoolsGrantable = pools?.filter(
    (pool) =>
      pool.isTerminated && new Date(pool?.terminationDate)?.getTime() <= new Date().getTime(),
  );
  const equityPoolsGranted = [...equityPoolGrantable, ...terminatedPoolsGrantable];
  const totalEquityPlansGrantable = calcTotal(
    pools.filter(
      (pool) =>
        !pool.isTerminated || new Date(pool?.terminationDate)?.getTime() > new Date().getTime(),
    ),
    ({ dilluted }) => toNumber(dilluted),
  );

  const issuedPercentageTotal = calcTotal(
    stakeholderTypesWithDiluted,
    ({ issuedPercentage }) => issuedPercentage,
  );

  const dilutedPercentageTotal = calcTotal(
    stakeholderTypesWithDiluted,
    ({ dilutedPercentage }) => dilutedPercentage,
  );

  const equityPoolsGrantedDilutedShares = getEquityPoolsGrantedDilutedShares({
    dilutedTotal,
    dilutedInOtherRows: calcTotal(stakeholderTypesWithDiluted, ({ diluted }) => diluted),
    grantablePoolDiluted: totalEquityPlansGrantable,
  });
  const equityPoolsGrantedDilutedPercentage =
    dilutedTotal > 0 ? equityPoolsGrantedDilutedShares / dilutedTotal : 0;

  const votingRightPercentageTotal = calcTotal(
    stakeholderTypesWithDiluted,
    ({ votingPercentage }) => votingPercentage,
  );

  const votingRightTotal = calcTotal(stakeholderTypesWithDiluted, ({ votingRight }) => votingRight);

  const commonStockTotal =
    toNumber(defaultShareClass?.issued) +
    calcTotal(
      equityPoolGrantable.filter(({ shareClassId }) => shareClassId === defaultShareClassId),
      ({ issued, unvested }) => issued + unvested,
    ) +
    calcTotal(
      terminatedPoolsGrantable.filter(({ shareClassId }) => shareClassId === defaultShareClassId),
      ({ unvested }) => unvested,
    );

  const convertedTotal = calcTotal(stakeholderTypesWithDiluted, ({ converted }) => converted);
  const convertedPercentageTotal = calcTotal(
    stakeholderTypesWithDiluted,
    ({ convertedPercentage }) => convertedPercentage,
  );

  const visibleOtherShareClasses = otherShareClasses
    .filter(({ diluted }) => diluted)
    .sort((a, b) => a.name.localeCompare(b.name));
  const shareClassesTotal = visibleOtherShareClasses.map(({ issued, id }) => {
    const issuedAmount = issued + getPoolShareClassAmountTotal(equityPoolGrantable, id, 'issued');
    const unvestedAmount =
      getPoolShareClassAmountTotal(equityPoolGrantable, id, 'unvested') +
      getPoolShareClassAmountTotal(terminatedPoolsGrantable, id, 'unvested');
    return issuedAmount + unvestedAmount;
  });

  const investmentTotal = calcTotal(stakeholdersWithDiluted, ({ invested }) => invested);
  const dividendsTotal = calcTotal(stakeholdersWithDiluted, ({ dividends }) => toNumber(dividends));

  const allShareClassNames = visibleOtherShareClasses.map(({ name }) => name);

  useEffect(() => {
    setEmptySearchResult(isSearchWithoutMatches(search, stakeholderTypesWithDiluted.length));
  }, [search, stakeholderTypesWithDiluted.length, setEmptySearchResult]);

  return (
    <>
      {stakeholderTypesWithDiluted.map(
        (
          {
            avgSharePrice,
            diluted,
            dilutedPercentage,
            issued,
            issuedPercentage,
            type,
            votingPercentage,
            shareClasses,
            votingRight,
            converted,
            convertedPercentage,
          },
          i,
        ) => {
          const stakeholders = stakeholdersWithDiluted.filter(
            (stakeholder) => stakeholder.type === type,
          );
          const invested = calcTotal(stakeholders, ({ invested }) => invested);
          const dividends = calcTotal(stakeholders, ({ dividends }) => toNumber(dividends));

          const { defaultShareClass, otherShareClasses } = getShareClasses(
            defaultShareClassId,
            shareClasses,
          );

          const otherShareClassesMap = otherShareClasses
            .sort((a, b) => a.name.localeCompare(b.name))
            .reduce<Record<string, (typeof otherShareClasses)[0]>>((acc, curr) => {
              acc[curr.name] = curr;
              return acc;
            }, {});

          return (
            <StakeholderTableItem
              allShareClassNames={allShareClassNames}
              fields={{
                [Column.NAME]: null,
                [Column.STAKEHOLDER_NAME]: null,
                [Column.TYPE]: messagesLocale[stakeholderTypeTranslation[type]],
                [Column.ISSUED]: toRound(issuedPercentage * 100),
                [Column.DILUTED]: toRound(dilutedPercentage * 100),
                [Column.VOTING]: toRound(votingPercentage * 100),
                [Column.NO_ISSUED]: issued,
                [Column.DILUTED_SHARES]: diluted,
                [Column.COMMON_STOCK]: toNumber(defaultShareClass?.issued),
                [Column.OTHER_SHARE_CLASSES]: allShareClassNames.map((name) => {
                  return otherShareClassesMap[name] ? otherShareClassesMap[name].issued : 0;
                }),
                [Column.VOTING_RIGHTS]: toNumber(votingRight).toLocaleString('en-US'),
                [Column.INVESTMENT]: invested.toLocaleString('en-US'),
                [Column.DIVIDENDS]: formatDividendAmount(toNumber(dividends)),
                [Column.AVERAGE_SHARE_PRICE]: toRound(avgSharePrice),
                [Column.CONVERTED_SHARES]: converted,
                [Column.CONVERTED_PERCENTAGE]: `${toRound(convertedPercentage * 100)}`,
              }}
              key={`${i}_${issued}${type}`}
              viewBy={viewBy}
              visibleColumns={visibleColumns}
            />
          );
        },
      )}

      <StakeholderTableItem
        allShareClassNames={allShareClassNames}
        fields={{
          [Column.NAME]: messagesLocale['EQUITY_POOLS_GRANTABLE'],
          [Column.STAKEHOLDER_NAME]: messagesLocale['EQUITY_POOLS_GRANTABLE'],
          [Column.TYPE]: messagesLocale['EQUITY_POOLS_GRANTABLE'],
          [Column.ISSUED]: toRound(
            calcTotal(equityPoolGrantable, ({ issuedPercentage }) => issuedPercentage) * 100,
          ),
          [Column.DILUTED]: toRound(
            calcTotal(equityPoolGrantable, ({ dillutedPercentage }) => dillutedPercentage) * 100,
          ),
          [Column.NO_ISSUED]: calcTotal(equityPoolGrantable, ({ issued }) => issued),
          [Column.VOTING]: 0,
          [Column.DILUTED_SHARES]: totalEquityPlansGrantable,
          [Column.COMMON_STOCK]: calcTotal(
            equityPoolGrantable.filter(({ shareClassId }) => shareClassId === defaultShareClassId),
            ({ issued }) => issued,
          ),
          [Column.OTHER_SHARE_CLASSES]: visibleOtherShareClasses.map(({ id }) =>
            getPoolShareClassAmountTotal(equityPoolGrantable, id, 'issued'),
          ),
          [Column.INVESTMENT]: 0,
          [Column.DIVIDENDS]: 0,
          [Column.AVERAGE_SHARE_PRICE]: 0,
          [Column.VOTING_RIGHTS]: 0,
          [Column.CONVERTED_SHARES]: calcTotal(equityPoolGrantable, ({ converted }) => converted),
          [Column.CONVERTED_PERCENTAGE]: toRound(
            calcTotal(equityPoolGrantable, ({ convertedPercentage }) => convertedPercentage) * 100,
          ),
        }}
        search={search}
        stakeholders={equityPoolGrantable.map(
          ({
            dilluted,
            id,
            name,
            dillutedPercentage,
            issued,
            issuedPercentage,
            converted,
            convertedPercentage,
            shareClassId,
          }) => ({
            id: id,
            name: name,
            diluted: dilluted,
            dilutedPercentage: dillutedPercentage,
            invested: 0,
            dividends: 0,
            issued,
            issuedPercentage,
            shareClasses: mapCapTablePoolShareClassAmounts(shareClasses, shareClassId, issued),
            type: StakeholderType.VCS,
            votingPercentage: 0,
            votingRight: 0,
            avgSharePrice: 0,
            converted,
            convertedPercentage,
          }),
        )}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      <StakeholderTableItem
        allShareClassNames={allShareClassNames}
        fields={{
          [Column.NAME]: 'Equity Pools Granted',
          [Column.STAKEHOLDER_NAME]: 'Equity Pools Granted',
          [Column.TYPE]: 'Equity Pools Granted',
          [Column.ISSUED]: toRound(
            calcTotal(
              [...equityPoolGrantable, ...terminatedPoolsGrantable],
              ({ unvestedPercentage }) => unvestedPercentage,
            ) * 100,
          ),
          [Column.DILUTED]: toRound(equityPoolsGrantedDilutedPercentage * 100),
          [Column.NO_ISSUED]: calcTotal(equityPoolsGranted, ({ unvested }) => unvested),
          [Column.VOTING]: 0,
          [Column.DILUTED_SHARES]: equityPoolsGrantedDilutedShares,
          [Column.COMMON_STOCK]: calcTotal(
            equityPoolsGranted.filter(({ shareClassId }) => shareClassId === defaultShareClassId),
            ({ unvested }) => unvested,
          ),
          [Column.OTHER_SHARE_CLASSES]: visibleOtherShareClasses.map(({ id }) =>
            getPoolShareClassAmountTotal(equityPoolsGranted, id, 'unvested'),
          ),
          [Column.INVESTMENT]: 0,
          [Column.DIVIDENDS]: 0,
          [Column.AVERAGE_SHARE_PRICE]: 0,
          [Column.VOTING_RIGHTS]: 0,
          [Column.CONVERTED_SHARES]: 0,
          [Column.CONVERTED_PERCENTAGE]: 0,
        }}
        search={search}
        stakeholders={equityPoolsGranted.map((pool) => {
          const poolGrantedDiluted = getPoolGrantedRowDilutedShares(
            pool,
            equityPoolsGrantedDilutedShares,
            equityPoolGrantable,
          );

          return {
            id: pool.id,
            name: pool.name,
            diluted: poolGrantedDiluted,
            dilutedPercentage: dilutedTotal > 0 ? poolGrantedDiluted / dilutedTotal : 0,
            invested: 0,
            dividends: 0,
            issued: pool.unvested,
            issuedPercentage: pool.unvestedPercentage,
            shareClasses: mapCapTablePoolShareClassAmounts(
              shareClasses,
              pool.shareClassId,
              pool.unvested,
            ),
            type: StakeholderType.VCS,
            votingPercentage: 0,
            votingRight: 0,
            avgSharePrice: 0,
            converted: 0,
            convertedPercentage: 0,
          };
        })}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />

      <StakeholderTableItem
        allShareClassNames={allShareClassNames}
        className="font-[550] text-gray-800"
        fields={{
          [Column.NAME]: null,
          [Column.STAKEHOLDER_NAME]: null,
          [Column.TYPE]: 'Total',
          [Column.ISSUED]: toRound(
            (issuedPercentageTotal +
              calcTotal(
                equityPoolGrantable,
                ({ issuedPercentage, unvestedPercentage }) => issuedPercentage + unvestedPercentage,
              ) +
              calcTotal(terminatedPoolsGrantable, ({ unvestedPercentage }) => unvestedPercentage)) *
              100,
          ),
          [Column.DILUTED]: toRound(
            (dilutedPercentageTotal +
              calcTotal(equityPoolGrantable, ({ dillutedPercentage }) => dillutedPercentage) +
              equityPoolsGrantedDilutedPercentage) *
              100,
          ),
          [Column.VOTING]: toRound(votingRightPercentageTotal * 100),
          [Column.NO_ISSUED]: issuedTotal.toLocaleString('en-US'),
          [Column.DILUTED_SHARES]: dilutedTotal.toLocaleString('en-US'),
          [Column.VOTING_RIGHTS]: votingRightTotal.toLocaleString('en-US'),
          [Column.COMMON_STOCK]: commonStockTotal.toLocaleString('en-US'),
          [Column.OTHER_SHARE_CLASSES]: shareClassesTotal,
          [Column.INVESTMENT]: investmentTotal.toLocaleString('en-US'),
          [Column.DIVIDENDS]: formatDividendAmount(dividendsTotal),
          [Column.AVERAGE_SHARE_PRICE]: '-',
          [Column.CONVERTED_SHARES]:
            convertedTotal + calcTotal(equityPoolGrantable, ({ converted }) => converted),
          [Column.CONVERTED_PERCENTAGE]: toRound(
            (convertedPercentageTotal +
              calcTotal(equityPoolGrantable, ({ convertedPercentage }) => convertedPercentage)) *
              100,
          ),
        }}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
    </>
  );
};
